import { useEffect, useState, type FormEvent } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { apiFetch, isApiError } from '../api/client'
import { AuthCard } from '../components/AuthCard'
import { useSession } from '../context/SessionContext'

type ResetState = 'idle' | 'loading' | 'error' | 'expired'

const MIN_PASSWORD = 8

// Reads the recovery token_hash once from `?token_hash=…&type=recovery` (the Supabase
// recovery email and the admin "reset link" both land here in that form).
function readTokenHash(search: string): string | null {
  const v = new URLSearchParams(search).get('token_hash')
  return v && v.trim() ? v.trim() : null
}

export function ResetPasswordView() {
  const location = useLocation()
  const navigate = useNavigate()
  const { status, recheck } = useSession()
  // Held in component state only; the address bar is cleaned below.
  const [tokenHash] = useState(() => readTokenHash(location.search))
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [state, setState] = useState<ResetState>(tokenHash ? 'idle' : 'expired')
  const [errorMsg, setErrorMsg] = useState('')

  // Strip the token (and any GoTrue #fragment) from the URL as soon as it is read.
  useEffect(() => {
    if (location.search || location.hash) navigate('/reset-password', { replace: true })
  }, [location.search, location.hash, navigate])

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!tokenHash) return
    if (password.length < MIN_PASSWORD) {
      setState('error')
      setErrorMsg(`Password must be at least ${MIN_PASSWORD} characters.`)
      return
    }
    if (password !== confirm) {
      setState('error')
      setErrorMsg('The passwords don’t match.')
      return
    }
    setState('loading')
    setErrorMsg('')
    try {
      await apiFetch('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token_hash: tokenHash, new_password: password }),
      })
      // Every session (this browser's too) was revoked; no cookie was issued.
      navigate('/login?reset=1', { replace: true })
      if (status === 'authenticated') recheck()
    } catch (err) {
      const s = isApiError(err) ? err.status : 0
      if (s === 401) {
        setState('expired')
        return
      }
      setState('error')
      if (s === 422) {
        const d = isApiError(err) && typeof err.detail === 'string' ? err.detail : ''
        setErrorMsg(d || `Password does not meet requirements (at least ${MIN_PASSWORD} characters).`)
      } else setErrorMsg('Could not reset your password. Please try again in a moment.')
    }
  }

  const footer = <Link to="/login">Back to sign in</Link>

  if (state === 'expired') {
    return (
      <AuthCard title="Link expired" footer={footer}>
        <p role="alert" className="form-error">
          This reset link has expired, was already used, or is incomplete.
        </p>
        <Link to="/forgot-password" className="btn btn--primary btn--block">Send a new link</Link>
      </AuthCard>
    )
  }

  const busy = state === 'loading'
  return (
    <AuthCard
      title="Choose a new password"
      subtitle="Resetting your password signs you out everywhere."
      footer={footer}
    >
      <form onSubmit={handleSubmit} aria-label="Reset password form" noValidate>
        <div className="field">
          <label htmlFor="reset-password">New password</label>
          <input id="reset-password" type="password" autoComplete="new-password" value={password}
            onChange={(e) => setPassword(e.target.value)} disabled={busy} required minLength={MIN_PASSWORD}
            aria-describedby="reset-password-hint" />
          <span id="reset-password-hint" className="field-hint">At least {MIN_PASSWORD} characters.</span>
        </div>
        <div className="field">
          <label htmlFor="reset-confirm">Confirm new password</label>
          <input id="reset-confirm" type="password" autoComplete="new-password" value={confirm}
            onChange={(e) => setConfirm(e.target.value)} disabled={busy} required />
        </div>
        {state === 'error' && <p role="alert" className="form-error">{errorMsg}</p>}
        <button type="submit" className="btn btn--primary btn--block"
          disabled={busy || !password || !confirm} aria-busy={busy}>
          {busy ? 'Saving…' : 'Set new password'}
        </button>
      </form>
    </AuthCard>
  )
}
